import { FileUpload as ExportIcon } from '@mui/icons-material';
import { Box, Button, Chip, Fade, TextField, Typography } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import {
  ChangeEvent,
  ComponentType,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FlexColumnCenter,
  FlexRowCenter,
  PulseDot,
  PulseDotSmall,
  ScrollableContainer,
  StickyFooter,
} from '../../../components/LayoutBoxes';
import { TAB_IDS } from '../../../constants/app';
import { AppContext } from '../../../context/AppContext';
import { useAutoExportDocument } from '../../../features/Document/hooks/useAutoExportDocument';
import { createDocumentState } from '../../../features/Document/utils/documentContentUtils';
import { useFontScale } from '../../../hooks/useFontScale';
import { FEATURE_FLAGS } from '../../../types/featureFlags';
import {
  hasFeature,
  useUpdateNoteButtonLabel,
} from '../../../utils/feature-util';
import { useTranscriptSave } from '../hooks/useTranscriptSave';
import {
  createChipStyles,
  transcriptPanelStyles as styles,
} from '../styles/transcriptPanelStyles';

const MAX_TRANSCRIPT_EDIT_CHARS = 100000;
const SHOW_TRANSCRIPT_CHAR_COUNT_AT = 50000;

interface TranscriptPanelProps {
  isRecording?: boolean;
  isPaused?: boolean;
  handleTabChange?: (tab: string) => void;
}

export const TranscriptPanel: ComponentType<TranscriptPanelProps> = ({
  isRecording = false,
  isPaused = false,
  handleTabChange,
}) => {
  const { sessionModel, generateDocument, updateSessionModel, user } =
    useContext(AppContext);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const lastTranscriptLength = useRef(sessionModel.transcript.length);
  const hasInitializedAutoScroll = useRef(false);
  const { scaleFontSize } = useFontScale();
  const chipStyles = useMemo(
    () => createChipStyles(scaleFontSize),
    [scaleFontSize]
  );
  const { performAutoExport, isAutoExportEnabled, autoExportConfig } =
    useAutoExportDocument();
  const [isReexporting, setIsReexporting] = useState(false);

  const hideSoapNotesTab = autoExportConfig?.hideSoapNotesTab === true;
  const flatTranscriptView = autoExportConfig?.flatTranscriptView === true;

  const showReexportError =
    sessionModel.generationError &&
    sessionModel.transcript.length > 0 &&
    hideSoapNotesTab &&
    !isRecording;

  const hasContentToExport =
    sessionModel.transcript.length > 0 || sessionModel.documents.length > 0;
  const showReexportButton =
    hideSoapNotesTab &&
    !isRecording &&
    !sessionModel.generationError &&
    !sessionModel.isGenerating &&
    !sessionModel.bgJobPending &&
    hasContentToExport;

  const handleReexport = useCallback(async () => {
    setIsReexporting(true);

    updateSessionModel((prev) => ({
      ...prev,
      generationError: false,
      isGenerating: true,
      bgJobPending: false,
    }));

    try {
      const existingDocument = sessionModel.documents.find(
        (doc) => doc.templateId === sessionModel.selectedTemplateId
      );

      let docStateToExport;

      if (existingDocument) {
        docStateToExport = createDocumentState(existingDocument);
      } else {
        const generatedDocument = await generateDocument({
          templateId: sessionModel.selectedTemplateId,
        });

        if (generatedDocument) {
          docStateToExport = createDocumentState(generatedDocument);
        }
      }

      if (docStateToExport && isAutoExportEnabled) {
        performAutoExport(docStateToExport);
      }
    } catch {
      // Error state will be set by generateDocument
    } finally {
      setIsReexporting(false);
      updateSessionModel((prev) => ({
        ...prev,
        isGenerating: false,
        bgJobPending: false,
      }));
    }
  }, [
    updateSessionModel,
    sessionModel.documents,
    sessionModel.selectedTemplateId,
    generateDocument,
    isAutoExportEnabled,
    performAutoExport,
  ]);

  const isTranscriptEditingEnabled = hasFeature(
    FEATURE_FLAGS.TRANSCRIPT_EDITING
  );
  const updateNoteButtonLabel = useUpdateNoteButtonLabel();

  const { saveTranscript, isSaving, saveError, clearSaveError } =
    useTranscriptSave({
      transcriptDocumentId: sessionModel.transcriptDocumentId,
    });

  const savedText = useMemo(() => {
    if (sessionModel.editOverrides?.__fullText__?.text) {
      return sessionModel.editOverrides.__fullText__.text;
    }
    return (
      sessionModel.transcript
        .map((line, index) => {
          const segId = line.segmentId || `segment-${index}`;
          return sessionModel.editOverrides?.[segId]?.text ?? line.text;
        })
        .filter((text) => text != null)
        .join(' ') || ''
    );
  }, [sessionModel.transcript, sessionModel.editOverrides]);

  const [editingText, setEditingText] = useState(savedText);

  useEffect(() => {
    setEditingText(savedText);
  }, [savedText]);

  const editingLength = editingText.length;
  const isAtOrOverLimit = editingLength >= MAX_TRANSCRIPT_EDIT_CHARS;

  const handleEditingChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const newValue = e.target.value;
      // Accept a change within the cap, or any change that does not grow the
      // text — so an already-over-cap transcript stays editable (can be shrunk).
      if (
        newValue.length <= MAX_TRANSCRIPT_EDIT_CHARS ||
        newValue.length <= editingText.length
      ) {
        setEditingText(newValue);
        if (saveError) clearSaveError();
      }
    },
    [editingText, saveError, clearSaveError]
  );

  const isDirty = editingText !== savedText;
  // Block the save/regenerate action while over the cap — shrinking edits stay
  // allowed (see handleEditingChange), but the note can't be saved until the
  // text is within the backend's 100k limit.
  const showUpdateNote =
    isTranscriptEditingEnabled &&
    !isRecording &&
    isDirty &&
    editingLength <= MAX_TRANSCRIPT_EDIT_CHARS &&
    sessionModel.documents.length > 0;

  const handleUpdateNote = useCallback(async () => {
    if (editingText.length > MAX_TRANSCRIPT_EDIT_CHARS) return;

    const editedAt = new Date().toISOString();
    const editedBy = user?.userId || 'unknown';

    const saved = await saveTranscript({
      editOverrides: {
        __fullText__: { text: editingText, editedAt, editedBy },
      },
    });
    if (!saved) return;

    updateSessionModel((prev) => ({
      ...prev,
      transcriptIsEdited: true,
      transcriptUpdatedAt: editedAt,
      transcriptUpdatedBy: editedBy,
      editOverrides: {
        ...prev.editOverrides,
        __fullText__: { text: editingText, editedAt, editedBy },
      },
    }));

    if (!autoExportConfig?.hideSoapNotesTab) {
      handleTabChange?.(TAB_IDS.document);
    }

    const generatedDocument = await generateDocument({
      templateId: sessionModel.selectedTemplateId,
      transcript: editingText,
      clearExistingDocuments: true,
    });

    if (generatedDocument && isAutoExportEnabled) {
      const documentState = createDocumentState(generatedDocument);
      performAutoExport(documentState);
    }
  }, [
    editingText,
    saveTranscript,
    updateSessionModel,
    user?.userId,
    generateDocument,
    sessionModel.selectedTemplateId,
    isAutoExportEnabled,
    performAutoExport,
    autoExportConfig?.hideSoapNotesTab,
    handleTabChange,
  ]);

  useEffect(() => {
    if (
      sessionModel.transcript.length > 0 &&
      shouldAutoScroll &&
      scrollContainerRef.current
    ) {
      const scrollContainer = scrollContainerRef.current;

      const scrollToBottom = () => {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth',
        });
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToBottom);
      });
    }
  }, [sessionModel.transcript.length, shouldAutoScroll]);

  useEffect(() => {
    const hasTranscript = sessionModel.transcript.length > 0;
    if (hasTranscript && !hasInitializedAutoScroll.current) {
      hasInitializedAutoScroll.current = true;
      if (!shouldAutoScroll) {
        setShouldAutoScroll(true);
      }
    }
  }, [sessionModel.transcript.length, shouldAutoScroll]);

  useEffect(() => {
    lastTranscriptLength.current = sessionModel.transcript.length;
  }, [sessionModel.transcript.length]);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
    setShouldAutoScroll(isNearBottom);
  };

  if (sessionModel.transcript.length === 0) {
    return (
      <FlexColumnCenter sx={styles.emptyContainer}>
        {isRecording ? (
          <>
            <PulseDot sx={{ backgroundColor: 'custom.recordingIndicator' }} />
            <Typography variant='body2'>Listening for speech...</Typography>
          </>
        ) : (
          <>
            <Typography variant='h6' sx={styles.emptyTitle}>
              No Transcript Yet
            </Typography>
            <Typography variant='body2' sx={styles.centeredText}>
              Start recording to see live transcription appear here.
            </Typography>
          </>
        )}
      </FlexColumnCenter>
    );
  }

  const isEditable =
    isTranscriptEditingEnabled &&
    !isRecording &&
    !sessionModel.isGenerating &&
    !isSaving;

  const flatTextElement = flatTranscriptView ? (
    <Fade in={true} timeout={300}>
      <Typography
        variant='body2'
        sx={{
          ...styles.flatTranscriptText,
          fontSize: scaleFontSize('14px'),
        }}
      >
        {savedText}
      </Typography>
    </Fade>
  ) : null;

  const showEditedNote =
    sessionModel.transcriptIsEdited &&
    (isTranscriptEditingEnabled || flatTranscriptView);

  const editedNoteElement = (
    <Typography
      role='status'
      data-testid='transcript-edited-note'
      sx={styles.editedNote}
    >
      Transcript has been edited
    </Typography>
  );

  if (isRecording && !isTranscriptEditingEnabled) {
    return (
      <ScrollableContainer
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={styles.scrollContainer}
      >
        {showEditedNote && editedNoteElement}
        {flatTranscriptView
          ? flatTextElement
          : sessionModel.transcript.map((segment, index) => {
              const segId = segment.segmentId || `segment-${index}`;
              const override = sessionModel.editOverrides?.[segId];
              const displayText = override?.text ?? segment.text;

              return (
                <Fade
                  key={segId}
                  in={true}
                  timeout={300}
                  style={{
                    transitionDelay: `${Math.min(index * 20, 80)}ms`,
                  }}
                >
                  <Box sx={styles.segmentWrapper}>
                    <Chip
                      variant='transcript'
                      label={displayText}
                      sx={chipStyles as Record<string, unknown>}
                    />
                  </Box>
                </Fade>
              );
            })}

        {!shouldAutoScroll && sessionModel.transcript.length > 0 && (
          <Fade in={true} timeout={300}>
            <Box
              onClick={() => {
                setShouldAutoScroll(true);
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollTo({
                    top: scrollContainerRef.current.scrollHeight,
                    behavior: 'smooth',
                  });
                }
              }}
              sx={styles.scrollToLatestButton}
            >
              <svg
                width='10'
                height='10'
                viewBox='0 0 24 24'
                fill='currentColor'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path d='M12 15.0006L5 8.00063L6.4 6.60063L12 12.2006L17.6 6.60063L19 8.00063L12 15.0006Z' />
              </svg>
              <span>Scroll to latest</span>
            </Box>
          </Fade>
        )}

        {!isPaused && (
          <FlexRowCenter sx={styles.processingContainer}>
            <PulseDotSmall />
            <Typography variant='caption' sx={{ ml: 0.5 }}>
              Processing new audio...
            </Typography>
          </FlexRowCenter>
        )}
      </ScrollableContainer>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <ScrollableContainer
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={styles.scrollContainer}
      >
        {showEditedNote && editedNoteElement}
        {isTranscriptEditingEnabled ? (
          <Box
            title={!isEditable ? 'Currently processing' : undefined}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1 }}
          >
            <TextField
              multiline
              fullWidth
              disabled={!isEditable}
              value={editingText}
              onChange={handleEditingChange}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: scaleFontSize('14px'),
                  lineHeight: 1.6,
                  padding: '8px 12px',
                  alignItems: 'flex-start',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                  borderRadius: '8px',
                },
              }}
            />
            {editingLength > SHOW_TRANSCRIPT_CHAR_COUNT_AT && (
              <Typography
                variant='caption'
                sx={
                  isAtOrOverLimit
                    ? styles.characterCountWarning
                    : styles.characterCount
                }
              >
                {editingLength.toLocaleString()} /{' '}
                {MAX_TRANSCRIPT_EDIT_CHARS.toLocaleString()} characters
              </Typography>
            )}
            {saveError && (
              <Typography sx={styles.saveErrorText}>{saveError}</Typography>
            )}
          </Box>
        ) : flatTranscriptView ? (
          flatTextElement
        ) : (
          sessionModel.transcript.map((segment, index) => {
            const segId = segment.segmentId || `segment-${index}`;

            return (
              <Fade
                key={segId}
                in={true}
                timeout={300}
                style={{
                  transitionDelay: `${Math.min(index * 20, 80)}ms`,
                }}
              >
                <Box sx={styles.segmentWrapper}>
                  <Chip
                    variant='transcript'
                    label={segment.text}
                    sx={chipStyles as Record<string, unknown>}
                  />
                </Box>
              </Fade>
            );
          })
        )}

        {/* Recording indicators when FF on */}
        {isRecording && isTranscriptEditingEnabled && (
          <>
            {!shouldAutoScroll && sessionModel.transcript.length > 0 && (
              <Fade in={true} timeout={300}>
                <Box
                  onClick={() => {
                    setShouldAutoScroll(true);
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollTo({
                        top: scrollContainerRef.current.scrollHeight,
                        behavior: 'smooth',
                      });
                    }
                  }}
                  sx={styles.scrollToLatestButton}
                >
                  <svg
                    width='10'
                    height='10'
                    viewBox='0 0 24 24'
                    fill='currentColor'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path d='M12 15.0006L5 8.00063L6.4 6.60063L12 12.2006L17.6 6.60063L19 8.00063L12 15.0006Z' />
                  </svg>
                  <span>Scroll to latest</span>
                </Box>
              </Fade>
            )}
            {!isPaused && (
              <FlexRowCenter sx={styles.processingContainer}>
                <PulseDotSmall />
                <Typography variant='caption' sx={{ ml: 0.5 }}>
                  Processing new audio...
                </Typography>
              </FlexRowCenter>
            )}
          </>
        )}

        {/* Re-export error banner - shows when generation fails and SOAP tab is hidden */}
        {showReexportError && (
          <Fade in={true} timeout={300}>
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'transparent',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                zIndex: 20,
              }}
            >
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                  fontSize: '14px',
                }}
              >
                Failed to generate and export your note. Please try again.
              </Typography>
              <Button
                variant='contained'
                onClick={handleReexport}
                disabled={isReexporting || sessionModel.isGenerating}
                sx={{
                  backgroundColor: 'error.main',
                  color: 'common.white',
                  borderRadius: '24px',
                  padding: '8px 24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: 'error.dark',
                  },
                  '&:disabled': {
                    backgroundColor: (theme: Theme) =>
                      alpha(theme.palette.error.main, 0.45),
                    color: 'common.white',
                  },
                }}
              >
                {isReexporting || sessionModel.isGenerating
                  ? 'Generating...'
                  : 'Retry'}
              </Button>
            </Box>
          </Fade>
        )}

        {/* General re-export button - shows when SOAP tab is hidden and there's content to export */}
        {showReexportButton && (
          <Fade in={true} timeout={300}>
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'custom.backgroundAlt',
                borderTop: '1px solid',
                borderColor: 'divider',
                p: 2,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 20,
              }}
            >
              <Button
                variant='contained'
                onClick={handleReexport}
                disabled={isReexporting}
                startIcon={<ExportIcon />}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'common.white',
                  borderRadius: '24px',
                  padding: '8px 24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&:disabled': {
                    backgroundColor: (theme: Theme) =>
                      theme.palette.primary.light,
                    color: 'common.white',
                  },
                }}
              >
                {isReexporting ? 'Exporting...' : 'Export to EHR'}
              </Button>
            </Box>
          </Fade>
        )}
      </ScrollableContainer>

      {/* Update Note button — outside scroll container, always at bottom */}
      {showUpdateNote && (
        <StickyFooter>
          <Button
            variant='pill'
            onClick={handleUpdateNote}
            disabled={isSaving || sessionModel.isGenerating}
          >
            {isSaving
              ? 'Saving...'
              : sessionModel.isGenerating
                ? 'Generating...'
                : updateNoteButtonLabel}
          </Button>
        </StickyFooter>
      )}
    </Box>
  );
};
